import { useState } from 'react';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { materialLight, materialDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { ClipboardCheckmark24Filled, ClipboardCode24Filled } from '@fluentui/react-icons';

import styles from './MarkdownRenderer.module.css';

interface Props {
    content: string;
    isStreaming: boolean;
    useDarkMode: boolean;
};

export const MarkdownRenderer = ({ content, isStreaming, useDarkMode }: Props) => {
    function CopyCodeButton({ children }: { children: string | string[] }) {
        const [isCodeCopied, setIsCodeCopied] = useState(false);
        const text = Array.isArray(children) ? children.join("") : children;

        const handleClick = async () => {
            navigator.clipboard.writeText(text);

            setIsCodeCopied(true);
            setTimeout(() => {
                setIsCodeCopied(false);
            }, 2000); // Change back after 2 seconds
        }

        return (
            <div
                className={isStreaming ? styles.copyCodeButtonDisabled : styles.copyCodeButton}
                onClick={handleClick}
                title="Copy code block to clipboard" >
                {isCodeCopied ? <ClipboardCheckmark24Filled /> : <ClipboardCode24Filled />}
            </div>
        )
    };

    return (
        <Markdown
            className={useDarkMode ? styles.answerTextDark : styles.answerText}
            children={content}
            rehypePlugins={[rehypeRaw]}
            remarkPlugins={[remarkGfm]}
            components={{
                code({ children, className, node, ...props }) {
                    const hasLang = /language-(\w+)/.exec(className || "");
                    const language = hasLang ? hasLang[1] : undefined;
                    return hasLang ? (
                        <div className={styles.answerCodeBlock}>
                            <CopyCodeButton children={String(children).replace(/\n$/, "")} />
                            <SyntaxHighlighter
                                language={language}
                                style={useDarkMode ? materialDark : materialLight}
                                wrapLongLines
                                children={String(children).replace(/\n$/, "")}
                            />
                        </div>
                    ) : (
                        <code {...props} className={className}>
                            {children}
                        </code>
                    );
                }
            }}
        />
    );
};